import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { X, Lock, Unlock, Users, Calendar as CalendarIcon, DollarSign, Percent, Trash2, Eye, EyeOff, Sparkles, Lightbulb, ArrowUp, ArrowDown } from 'lucide-react';
import { calendarAPI } from '../services/api';
import CalendarSync from './CalendarSync';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const PropertyCalendar = ({ propertyId, propertyTitle }) => {
  const { toast } = useToast();
  const { getAuthToken } = useAuth();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedDates, setSelectedDates] = useState({ start: '', end: '' });
  const [blockReason, setBlockReason] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Price override state
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceOverrideType, setPriceOverrideType] = useState('fixed'); // fixed or percentage
  const [priceOverrideValue, setPriceOverrideValue] = useState(100);
  const [priceOverrideReason, setPriceOverrideReason] = useState('');
  const [modalMode, setModalMode] = useState('block'); // block or price
  const [priceOverrides, setPriceOverrides] = useState([]);

  // Per-date pricing display
  const [showPrices, setShowPrices] = useState(true);
  const [baseRate, setBaseRate] = useState(0);
  const [datePriceMap, setDatePriceMap] = useState(new Map());

  // Smart Pricing AI suggestions
  const [showSmartPricing, setShowSmartPricing] = useState(false);
  const [smartPricingData, setSmartPricingData] = useState(null);
  const [smartPriceMap, setSmartPriceMap] = useState(new Map());
  const [smartPricingLoading, setSmartPricingLoading] = useState(false);
  const [selectedSmartTip, setSelectedSmartTip] = useState(null);

  // Build a YYYY-MM-DD -> nightly rate map for past confirmed/completed
  // bookings. Used to render the price actually charged on each historical
  // night so hosts can pull it up as evidence (taxes, reviews, disputes).
  const pastBookingPriceMap = useMemo(() => {
    const map = new Map();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    events.forEach((ev) => {
      const type = ev.extendedProps?.type;
      const status = ev.extendedProps?.status;
      const pricing = ev.extendedProps?.pricing;
      if (type !== 'booking') return;
      if (!pricing || typeof pricing !== 'object') return;
      const start = new Date(ev.start);
      const end = new Date(ev.end);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;

      // Total accommodation / nights — fall back to whichever fields exist.
      const totalAccommodation = pricing.accommodation
        ?? pricing.totalAccommodation
        ?? pricing.subtotal
        ?? null;
      const explicitNightly = pricing.nightlyRate ?? null;

      const nights = Math.max(1, Math.round((end - start) / 86400000));
      let nightlyRate = null;
      if (typeof explicitNightly === 'number') {
        nightlyRate = explicitNightly;
      } else if (typeof totalAccommodation === 'number') {
        nightlyRate = totalAccommodation / nights;
      }
      if (nightlyRate == null) return;

      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        if (d < today) {
          const key = d.toLocaleDateString('en-CA');
          if (!map.has(key)) {
            map.set(key, {
              nightlyRate: Math.round(nightlyRate),
              status: status || 'completed',
              guestName: ev.extendedProps?.guestName || 'Guest',
            });
          }
        }
      }
    });
    return map;
  }, [events]);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await calendarAPI.getPropertyCalendar(propertyId, token);

      // API now returns { events: [...], baseRate: number }
      const data = response.events || response; // Fallback if still array
      const fetchedBaseRate = response.baseRate || 0;
      setBaseRate(fetchedBaseRate);

      // Transform data for FullCalendar
      const calendarEvents = data.map(event => {
        if (event.type === 'price_override') {
          return {
            id: event._id,
            title: `£${event.price}`,
            start: event.startDate,
            end: event.endDate,
            allDay: true,
            backgroundColor: '#8b5cf6',
            borderColor: '#7c3aed',
            textColor: '#ffffff',
            extendedProps: {
              type: 'price_override',
              price: event.price,
              overrideType: event.overrideType,
              reason: event.reason
            }
          };
        }

        // Booking color mapping: confirmed=green, pending=amber, completed=navy blue
        const bookingColors = {
          confirmed: { bg: '#22c55e', border: '#16a34a' },
          pending: { bg: '#f59e0b', border: '#d97706' },
          pending_payment: { bg: '#f59e0b', border: '#d97706' },
          completed: { bg: '#1F3A68', border: '#162d52' }  // navy blue for past completed
        };
        const colors = event.type === 'booking'
          ? (bookingColors[event.status] || bookingColors.pending)
          : { bg: '#9ca3af', border: '#6b7280' };

        return {
          id: event._id,
          title: event.type === 'booking'
            ? `${event.guestName || 'Guest'}`
            : event.reason || 'Blocked',
          start: event.startDate,
          end: event.endDate,
          allDay: true,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          textColor: '#ffffff',
          extendedProps: {
            type: event.type,
            status: event.status,
            guestName: event.guestName,
            guestEmail: event.guestEmail,
            numberOfGuests: event.numberOfGuests,
            pricing: event.pricing,
            reason: event.reason
          }
        };
      });

      setEvents(calendarEvents);

      // Extract price overrides and build date-price map
      const overrideEvents = data.filter(e => e.type === 'price_override');
      setPriceOverrides(overrideEvents);

      // Build a map of date -> { price, isOverride } for the per-day display
      const priceMap = new Map();
      overrideEvents.forEach(override => {
        const start = new Date(override.startDate);
        const end = new Date(override.endDate);
        const current = new Date(start);
        while (current < end) {
          const dateStr = current.toISOString().split('T')[0];
          priceMap.set(dateStr, { price: override.price, isOverride: true });
          current.setDate(current.getDate() + 1);
        }
      });
      setDatePriceMap(priceMap);
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast({ title: 'Error', description: 'Failed to load calendar data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [propertyId, getAuthToken, toast]);

  const fetchSmartPricing = useCallback(async () => {
    if (!propertyId) return;
    setSmartPricingLoading(true);
    try {
      const token = getAuthToken();
      const data = await calendarAPI.getSmartPricing(propertyId, token);
      setSmartPricingData(data);

      // Build a quick-lookup map: date -> suggestion
      const spMap = new Map();
      (data.suggestions || []).forEach(s => {
        spMap.set(s.date, s);
      });
      setSmartPriceMap(spMap);
    } catch (error) {
      console.error('Error fetching smart pricing:', error);
      toast({
        title: 'Smart Pricing',
        description: 'Could not load AI pricing suggestions. Try again later.',
        variant: 'destructive'
      });
      setShowSmartPricing(false);
    } finally {
      setSmartPricingLoading(false);
    }
  }, [propertyId, getAuthToken, toast]);

  // Toggle handler for smart pricing — persists to backend
  const handleSmartPricingToggle = useCallback(async () => {
    const newVal = !showSmartPricing;
    setShowSmartPricing(newVal);
    if (newVal && smartPriceMap.size === 0) {
      fetchSmartPricing();
    }
    // Persist to backend so booking calculations use smart pricing
    try {
      const token = getAuthToken();
      await calendarAPI.toggleSmartPricing(propertyId, newVal, token);
    } catch (err) {
      console.error('Failed to persist smart pricing toggle:', err);
    }
  }, [showSmartPricing, smartPriceMap.size, fetchSmartPricing, propertyId, getAuthToken]);

  // Load smart pricing status on mount
  useEffect(() => {
    if (propertyId) {
      fetchCalendarData();
      // Check if smart pricing was previously enabled
      (async () => {
        try {
          const token = getAuthToken();
          const status = await calendarAPI.getSmartPricingStatus(propertyId, token);
          if (status.enabled) {
            setShowSmartPricing(true);
            fetchSmartPricing();
          }
        } catch (err) {
          // Ignore — smart pricing status not critical
        }
      })();
    }
  }, [propertyId, fetchCalendarData, getAuthToken, fetchSmartPricing]);

  const handleDateSelect = (selectInfo) => {
    // Open combined modal with selected dates — let host choose block or pricing
    setSelectedDates({
      start: selectInfo.startStr,
      end: selectInfo.endStr
    });
    setModalMode('block'); // default tab
    setShowBlockModal(true);
  };

  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    setSelectedEvent({
      _id: event.id,
      type: event.extendedProps.type,
      title: event.title,
      startDate: event.startStr,
      endDate: event.endStr,
      status: event.extendedProps.status,
      guestName: event.extendedProps.guestName,
      guestEmail: event.extendedProps.guestEmail,
      numberOfGuests: event.extendedProps.numberOfGuests,
      pricing: event.extendedProps.pricing,
      reason: event.extendedProps.reason,
      price: event.extendedProps.price,
      overrideType: event.extendedProps.overrideType
    });
  };

  const handleBlockDates = async () => {
    if (!selectedDates.start || !selectedDates.end) {
      toast({ title: 'Error', description: 'Please select start and end dates', variant: 'destructive' });
      return;
    }

    try {
      const token = getAuthToken();
      await calendarAPI.blockDates({
        propertyId,
        startDate: selectedDates.start,
        endDate: selectedDates.end,
        reason: blockReason || 'Blocked by host'
      }, token);

      toast({ title: 'Success', description: 'Dates blocked successfully' });
      setShowBlockModal(false);
      setSelectedDates({ start: '', end: '' });
      setBlockReason('');
      fetchCalendarData();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to block dates',
        variant: 'destructive' 
      });
    }
  };

  const handleUnblockDates = async (blockId) => {
    try {
      const token = getAuthToken();
      await calendarAPI.unblockDates(blockId, token);
      toast({ title: 'Success', description: 'Dates unblocked successfully' });
      setSelectedEvent(null);
      fetchCalendarData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to unblock dates', variant: 'destructive' });
    }
  };

  const handlePriceOverride = async () => {
    if (!selectedDates.start || !selectedDates.end) {
      toast({ title: 'Error', description: 'Please select start and end dates', variant: 'destructive' });
      return;
    }

    try {
      const token = getAuthToken();
      const overrideData = {
        propertyId,
        startDate: selectedDates.start,
        endDate: selectedDates.end,
        overrideType: priceOverrideType,
        reason: priceOverrideReason
      };

      if (priceOverrideType === 'fixed') {
        overrideData.price = parseFloat(priceOverrideValue);
      } else {
        overrideData.percentageChange = parseFloat(priceOverrideValue);
      }

      await calendarAPI.createPriceOverride(overrideData, token);
      toast({ title: 'Success', description: 'Price override saved successfully' });
      setShowPriceModal(false);
      setSelectedDates({ start: '', end: '' });
      setPriceOverrideValue(100);
      setPriceOverrideReason('');
      fetchCalendarData();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save price override',
        variant: 'destructive' 
      });
    }
  };

  const handleDeletePriceOverride = async (overrideId) => {
    try {
      const token = getAuthToken();
      await calendarAPI.deletePriceOverride(overrideId, token);
      toast({ title: 'Success', description: 'Price override deleted' });
      setSelectedEvent(null);
      fetchCalendarData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete price override', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CalendarSync propertyId={propertyId} />
      {/* Header with Legend and Actions */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex gap-4 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span>Confirmed Booking</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-500 rounded"></div>
            <span>Pending Booking</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-400 rounded"></div>
            <span>Blocked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#1F3A68] rounded"></div>
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-violet-500 rounded"></div>
            <span>Price Override</span>
          </div>
          {showSmartPricing && (
            <>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-emerald-500 rounded flex items-center justify-center">
                  <ArrowUp size={10} className="text-white" />
                </div>
                <span>AI: Increase</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-500 rounded flex items-center justify-center">
                  <ArrowDown size={10} className="text-white" />
                </div>
                <span>AI: Decrease</span>
              </div>
            </>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPrices(prev => !prev)}
            className={`transition-colors ${showPrices ? 'text-[#1F3A68] border-[#1F3A68]/40' : 'text-gray-400'}`}
            title={showPrices ? 'Hide prices on calendar' : 'Show prices on calendar'}
          >
            {showPrices ? <Eye size={16} className="mr-1.5" /> : <EyeOff size={16} className="mr-1.5" />}
            Prices
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSmartPricingToggle}
            disabled={smartPricingLoading}
            className={`transition-colors ${showSmartPricing
              ? 'text-emerald-700 border-emerald-400 bg-emerald-50 hover:bg-emerald-100'
              : 'text-gray-400 hover:text-emerald-600 hover:border-emerald-300'}`}
            title={showSmartPricing ? 'Hide AI pricing suggestions' : 'Show AI-optimised pricing suggestions'}
          >
            {smartPricingLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-1.5" />
            ) : (
              <Sparkles size={16} className="mr-1.5" />
            )}
            Smart Pricing
          </Button>
          <Button onClick={() => { setModalMode('price'); setShowPriceModal(true); }} variant="outline" size="sm">
            <DollarSign size={16} className="mr-2" />
            Set Price Override
          </Button>
          <Button onClick={() => { setModalMode('block'); setShowBlockModal(true); }} size="sm" className="bg-[#1F3A68]">
            <Lock size={16} className="mr-2" />
            Block Dates
          </Button>
        </div>
      </div>

      {/* FullCalendar */}
      <div className="bg-white rounded-lg border p-4">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth'
          }}
          events={events}
          selectable={true}
          selectMirror={true}
          select={handleDateSelect}
          dayMaxEvents={3}
          weekends={true}
          eventClick={handleEventClick}
          height="auto"
          eventDisplay="block"
          displayEventTime={false}
          dayCellContent={(arg) => {
            const dateStr = arg.date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
            const priceInfo = datePriceMap.get(dateStr);
            const price = priceInfo ? priceInfo.price : baseRate;
            const isOverride = priceInfo?.isOverride || false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isPast = arg.date < today;

            // Smart pricing suggestion (hidden when showPrices is off)
            const smartSuggestion = (showSmartPricing && showPrices) ? smartPriceMap.get(dateStr) : null;

            // For past dates that had a booking, surface the nightly rate
            // actually charged. Useful as evidence (tax records, disputes,
            // performance reviews).
            const pastBookingInfo = isPast ? pastBookingPriceMap.get(dateStr) : null;

            return (
              <div className="flex flex-col items-center w-full">
                <span>{arg.dayNumberText}</span>
                {showPrices && price > 0 && !isPast && !smartSuggestion && (
                  <span className={`text-[10px] leading-none font-semibold ${
                    isOverride ? 'text-violet-600' : 'text-gray-400'
                  }`}>
                    £{Math.round(price)}
                  </span>
                )}
                {showPrices && pastBookingInfo && (
                  <span
                    className="text-[10px] leading-none font-semibold text-slate-500"
                    title={`Charged £${pastBookingInfo.nightlyRate} on this night for ${pastBookingInfo.guestName}`}
                  >
                    £{pastBookingInfo.nightlyRate}
                  </span>
                )}
                {smartSuggestion && !isPast && (
                  <span
                    className={`text-[10px] leading-none font-bold cursor-pointer ${
                      smartSuggestion.direction === 'increase'
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                    }`}
                    title={`AI suggests £${smartSuggestion.suggestedPrice} (${smartSuggestion.pctChange > 0 ? '+' : ''}${smartSuggestion.pctChange}%) — ${smartSuggestion.reasons.join(', ')}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSmartTip(smartSuggestion);
                    }}
                  >
                    {smartSuggestion.direction === 'increase' ? '▲' : '▼'}£{smartSuggestion.suggestedPrice}
                  </span>
                )}
              </div>
            );
          }}
          eventContent={(eventInfo) => (
            <div className="px-1 py-0.5 text-xs truncate">
              {eventInfo.event.extendedProps.type === 'booking' && (
                <Users size={10} className="inline mr-1" />
              )}
              {eventInfo.event.extendedProps.type === 'blocked' && (
                <Lock size={10} className="inline mr-1" />
              )}
              {eventInfo.event.title}
            </div>
          )}
        />
      </div>

      {/* Smart Pricing Summary Banner — hidden when prices toggle is off (screenshot mode) */}
      {showSmartPricing && showPrices && smartPricingData && !smartPricingLoading && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-emerald-600" />
              <h3 className="font-semibold text-emerald-800 text-sm">AI Smart Pricing Insights</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                {smartPricingData.summary?.totalSuggestions || 0} suggestions
              </span>
              <span className="text-xs text-white bg-emerald-600 px-2 py-0.5 rounded-full font-semibold">
                LIVE — Applied to bookings
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="bg-white/60 rounded-lg p-2">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Your Base Rate</p>
              <p className="text-base font-bold text-[#1F3A68]">£{smartPricingData.baseRate}</p>
            </div>
            <div className="bg-white/60 rounded-lg p-2">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Area Average</p>
              <p className="text-base font-bold text-[#1F3A68]">£{smartPricingData.areaAvgRate}</p>
            </div>
            <div className="bg-white/60 rounded-lg p-2">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">30-Day Occupancy</p>
              <p className="text-base font-bold text-[#1F3A68]">{smartPricingData.occupancyRate30Day}%</p>
            </div>
            <div className="bg-white/60 rounded-lg p-2">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Potential Gain</p>
              <p className="text-base font-bold text-emerald-600">
                +£{smartPricingData.summary?.potentialRevenueGain || 0}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
            <span className="flex items-center gap-1">
              <span className="text-emerald-600 font-bold">▲</span> Increase: {smartPricingData.summary?.priceIncreases || 0} dates
            </span>
            <span className="flex items-center gap-1">
              <span className="text-amber-600 font-bold">▼</span> Decrease: {smartPricingData.summary?.priceDecreases || 0} dates
            </span>
            <span className="ml-auto text-gray-400">
              Based on your analytics, booking patterns & local trends
            </span>
          </div>
        </div>
      )}

      {/* Smart Tip Detail Modal */}
      {selectedSmartTip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles size={18} className="text-emerald-600" />
                AI Pricing Suggestion
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setSelectedSmartTip(null)}>
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                  {new Date(selectedSmartTip.date + 'T00:00:00').toLocaleDateString('en-GB', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </p>
                <div className="flex items-center justify-center gap-4 mt-2">
                  <div>
                    <p className="text-xs text-gray-400">Current</p>
                    <p className="text-xl font-bold text-gray-500 line-through">£{selectedSmartTip.currentPrice}</p>
                  </div>
                  <div className={`text-2xl ${selectedSmartTip.direction === 'increase' ? 'text-emerald-500' : 'text-amber-500'}`}>
                    →
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Suggested</p>
                    <p className={`text-2xl font-bold ${
                      selectedSmartTip.direction === 'increase' ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      £{selectedSmartTip.suggestedPrice}
                    </p>
                  </div>
                </div>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedSmartTip.direction === 'increase'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {selectedSmartTip.pctChange > 0 ? '+' : ''}{selectedSmartTip.pctChange}%
                </span>
              </div>

              {/* Reasons */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Why this price?</p>
                {selectedSmartTip.reasons.map((reason, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Lightbulb size={14} className="text-amber-500 flex-shrink-0" />
                    <span className="text-gray-700">{reason}</span>
                  </div>
                ))}
              </div>

              {/* Factor Breakdown */}
              {selectedSmartTip.factors && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">Factor Breakdown</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {Object.entries({
                      'Day of Week': selectedSmartTip.factors.dayOfWeek,
                      'Seasonal': selectedSmartTip.factors.seasonal,
                      'Demand': selectedSmartTip.factors.demand,
                      'Gap Fill': selectedSmartTip.factors.gap,
                      'Lead Time': selectedSmartTip.factors.leadTime,
                      'Occupancy': selectedSmartTip.factors.occupancy,
                      'Area Comp.': selectedSmartTip.factors.area
                    }).map(([label, val]) => (
                      <div key={label} className="flex justify-between">
                        <span className="text-gray-500">{label}</span>
                        <span className={`font-semibold ${
                          val > 1.02 ? 'text-emerald-600' : val < 0.98 ? 'text-amber-600' : 'text-gray-600'
                        }`}>
                          {val > 1 ? '+' : ''}{((val - 1) * 100).toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Apply button */}
              <div className="flex gap-3 pt-1">
                <Button variant="outline" onClick={() => setSelectedSmartTip(null)} className="flex-1">
                  Dismiss
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => {
                    // Pre-fill the price override form with the suggested price
                    setSelectedDates({
                      start: selectedSmartTip.date,
                      end: (() => {
                        const d = new Date(selectedSmartTip.date + 'T00:00:00');
                        d.setDate(d.getDate() + 1);
                        return d.toISOString().split('T')[0];
                      })()
                    });
                    setPriceOverrideType('fixed');
                    setPriceOverrideValue(selectedSmartTip.suggestedPrice);
                    setPriceOverrideReason(`AI Smart Pricing — ${selectedSmartTip.reasons[0] || 'Optimised'}`);
                    setSelectedSmartTip(null);
                    setModalMode('price');
                    setShowBlockModal(true);
                  }}
                >
                  <DollarSign size={16} className="mr-1" />
                  Apply £{selectedSmartTip.suggestedPrice}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Combined Manage Dates Modal (Block + Pricing) */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon size={20} />
                Manage Dates
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowBlockModal(false)}>
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Range */}
              <div className="bg-gray-50 rounded-lg p-3">
                <Label className="text-xs text-gray-500 uppercase tracking-wider">Selected Range</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label className="text-sm">Start Date</Label>
                    <Input
                      type="date"
                      value={selectedDates.start}
                      onChange={(e) => setSelectedDates(prev => ({ ...prev, start: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">End Date</Label>
                    <Input
                      type="date"
                      value={selectedDates.end}
                      onChange={(e) => setSelectedDates(prev => ({ ...prev, end: e.target.value }))}
                      min={selectedDates.start || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
                {selectedDates.start && selectedDates.end && (
                  <p className="text-xs text-[#1F3A68] mt-2 font-medium">
                    {Math.ceil((new Date(selectedDates.end) - new Date(selectedDates.start)) / (1000 * 60 * 60 * 24))} night(s) selected
                  </p>
                )}
              </div>

              {/* Action Tabs */}
              <Tabs value={modalMode} onValueChange={setModalMode}>
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="block">
                    <Lock size={14} className="mr-2" /> Block Dates
                  </TabsTrigger>
                  <TabsTrigger value="price">
                    <DollarSign size={14} className="mr-2" /> Set Pricing
                  </TabsTrigger>
                </TabsList>

                {/* Block Tab */}
                <TabsContent value="block" className="space-y-3 mt-3">
                  <p className="text-sm text-gray-600">Prevent bookings during this period.</p>
                  <div>
                    <Label>Reason (optional)</Label>
                    <Textarea
                      placeholder="e.g., Personal use, Maintenance, Renovation..."
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => setShowBlockModal(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleBlockDates} className="flex-1 bg-[#1F3A68]">
                      <Lock size={16} className="mr-2" /> Block Dates
                    </Button>
                  </div>
                </TabsContent>

                {/* Price Tab */}
                <TabsContent value="price" className="space-y-3 mt-3">
                  <p className="text-sm text-gray-600">Override the nightly rate for these dates.</p>
                  <div>
                    <Label>Override Type</Label>
                    <div className="flex gap-3 mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={priceOverrideType === 'fixed' ? 'default' : 'outline'}
                        onClick={() => setPriceOverrideType('fixed')}
                        className={priceOverrideType === 'fixed' ? 'bg-[#1F3A68]' : ''}
                      >
                        <DollarSign size={14} className="mr-1" /> Fixed Price
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={priceOverrideType === 'percentage' ? 'default' : 'outline'}
                        onClick={() => setPriceOverrideType('percentage')}
                        className={priceOverrideType === 'percentage' ? 'bg-[#1F3A68]' : ''}
                      >
                        <Percent size={14} className="mr-1" /> Percentage
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>{priceOverrideType === 'fixed' ? 'Price per Night (£)' : 'Percentage Change (%)'}</Label>
                    <Input
                      type="number"
                      value={priceOverrideValue}
                      onChange={(e) => setPriceOverrideValue(e.target.value)}
                      min={priceOverrideType === 'fixed' ? 1 : -100}
                      placeholder={priceOverrideType === 'fixed' ? 'e.g., 150' : 'e.g., 20 for +20%'}
                    />
                    {priceOverrideType === 'percentage' && (
                      <p className="text-xs text-gray-500 mt-1">Positive = increase, negative = discount</p>
                    )}
                  </div>
                  <div>
                    <Label>Reason (optional)</Label>
                    <Input
                      placeholder="e.g., Weekend special, Holiday pricing..."
                      value={priceOverrideReason}
                      onChange={(e) => setPriceOverrideReason(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => setShowBlockModal(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={() => { handlePriceOverride(); setShowBlockModal(false); }} className="flex-1 bg-violet-600 hover:bg-violet-700">
                      <DollarSign size={16} className="mr-2" /> Save Pricing
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                {selectedEvent.type === 'booking' ? 'Booking Details' : 'Blocked Period'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setSelectedEvent(null)}>
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedEvent.type === 'booking' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      selectedEvent.status === 'confirmed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {selectedEvent.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-y-3 text-sm">
                    <span className="text-gray-600">Guest:</span>
                    <span className="font-semibold">{selectedEvent.guestName}</span>
                    
                    <span className="text-gray-600">Check-in:</span>
                    <span className="font-semibold">{selectedEvent.startDate}</span>
                    
                    <span className="text-gray-600">Check-out:</span>
                    <span className="font-semibold">{selectedEvent.endDate}</span>
                    
                    {selectedEvent.numberOfGuests && (
                      <>
                        <span className="text-gray-600">Guests:</span>
                        <span className="font-semibold">{selectedEvent.numberOfGuests}</span>
                      </>
                    )}
                    
                    {selectedEvent.pricing?.total && (
                      <>
                        <span className="text-gray-600">Total:</span>
                        <span className="font-bold text-[#1F3A68]">£{selectedEvent.pricing.total}</span>
                      </>
                    )}
                  </div>
                </div>
              ) : selectedEvent.type === 'price_override' ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-y-3 text-sm">
                    <span className="text-gray-600">From:</span>
                    <span className="font-semibold">{selectedEvent.startDate}</span>
                    
                    <span className="text-gray-600">To:</span>
                    <span className="font-semibold">{selectedEvent.endDate}</span>
                    
                    <span className="text-gray-600">Price:</span>
                    <span className="font-bold text-violet-600">£{selectedEvent.price}</span>
                    
                    <span className="text-gray-600">Type:</span>
                    <span className="font-semibold capitalize">{selectedEvent.overrideType}</span>
                    
                    {selectedEvent.reason && (
                      <>
                        <span className="text-gray-600">Reason:</span>
                        <span className="font-semibold">{selectedEvent.reason}</span>
                      </>
                    )}
                  </div>
                  
                  <Button 
                    onClick={() => handleDeletePriceOverride(selectedEvent._id)}
                    variant="outline"
                    className="w-full mt-4 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete Override
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-y-3 text-sm">
                    <span className="text-gray-600">From:</span>
                    <span className="font-semibold">{selectedEvent.startDate}</span>
                    
                    <span className="text-gray-600">To:</span>
                    <span className="font-semibold">{selectedEvent.endDate}</span>
                    
                    {selectedEvent.reason && (
                      <>
                        <span className="text-gray-600">Reason:</span>
                        <span className="font-semibold">{selectedEvent.reason}</span>
                      </>
                    )}
                  </div>
                  
                  <Button 
                    onClick={() => handleUnblockDates(selectedEvent._id)}
                    variant="outline"
                    className="w-full mt-4"
                  >
                    <Unlock size={16} className="mr-2" />
                    Unblock These Dates
                  </Button>
                </div>
              )}
              
              <Button variant="outline" onClick={() => setSelectedEvent(null)} className="w-full">
                Close
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Price Override Modal */}
      {showPriceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <DollarSign size={20} />
                Set Price Override
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowPriceModal(false)}>
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Override the nightly rate for specific dates.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={selectedDates.start}
                    onChange={(e) => setSelectedDates(prev => ({ ...prev, start: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="override-start-date"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={selectedDates.end}
                    onChange={(e) => setSelectedDates(prev => ({ ...prev, end: e.target.value }))}
                    min={selectedDates.start || new Date().toISOString().split('T')[0]}
                    data-testid="override-end-date"
                  />
                </div>
              </div>
              
              <div>
                <Label>Override Type</Label>
                <div className="flex gap-4 mt-2">
                  <Button 
                    type="button"
                    variant={priceOverrideType === 'fixed' ? 'default' : 'outline'}
                    onClick={() => setPriceOverrideType('fixed')}
                    className={priceOverrideType === 'fixed' ? 'bg-[#1F3A68]' : ''}
                    data-testid="override-type-fixed"
                  >
                    <DollarSign size={16} className="mr-1" />
                    Fixed Price
                  </Button>
                  <Button 
                    type="button"
                    variant={priceOverrideType === 'percentage' ? 'default' : 'outline'}
                    onClick={() => setPriceOverrideType('percentage')}
                    className={priceOverrideType === 'percentage' ? 'bg-[#1F3A68]' : ''}
                    data-testid="override-type-percentage"
                  >
                    <Percent size={16} className="mr-1" />
                    Percentage
                  </Button>
                </div>
              </div>
              
              <div>
                <Label>
                  {priceOverrideType === 'fixed' ? 'Price per Night (£)' : 'Percentage Change (%)'}
                </Label>
                <Input
                  type="number"
                  value={priceOverrideValue}
                  onChange={(e) => setPriceOverrideValue(e.target.value)}
                  min={priceOverrideType === 'fixed' ? 1 : -100}
                  placeholder={priceOverrideType === 'fixed' ? 'e.g., 150' : 'e.g., 20 for 20% increase'}
                  data-testid="override-value-input"
                />
                {priceOverrideType === 'percentage' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Use positive values for increase, negative for decrease (e.g., -20 for 20% off)
                  </p>
                )}
              </div>
              
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  placeholder="e.g., Weekend special, Holiday pricing..."
                  value={priceOverrideReason}
                  onChange={(e) => setPriceOverrideReason(e.target.value)}
                  data-testid="override-reason-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowPriceModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handlePriceOverride} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="save-price-override-btn">
                  Save Override
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PropertyCalendar;
